import React, {useMemo} from "react";
import {distinctByKey} from "nate-react-api-helpers";
import {Divider, FormControl, Grid, IconButton, InputLabel, MenuItem, Select} from "@mui/material";
import {api} from "../../../../../api/API";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {ProductFileLookup} from "../ProductFileLookup";
import {asPerTemplate, noPrep, OptionGroup, PrepOption} from "../PrepMenuOptions";
import {useSyncedRef} from "../../../../../misc/SyncedRef";

const blankArray = [] as any;

export function PrepMenu(props: {
    id: string;
    name: string;
    value: PrepOption;
    editKey: keyof PrepOption
    editKeyCustom: keyof PrepOption
    editKeyCustomName: keyof PrepOption
    groupKey: keyof Omit<OptionGroup, "category">;
    groupCustomKey: keyof Omit<OptionGroup, "category">;
    group: OptionGroup;
    onChange(value: Partial<PrepOption>): void;
}) {
    const group = props.group;

    const customOpts = group[props.groupCustomKey] as string[] || blankArray;
    const stdOpts = group[props.groupKey] as string[] || blankArray;
    const value = autoFix(props.value[props.editKey] as string)

    const [showTemplate, setShowTemplate] = React.useState(false);

    const v = props.value;
    const listOfFiles = useMemo(() => {
        return distinctByKey([
            {name: v.singleActiveDoorPrepCustomTemplateName, id: v.singleActiveDoorPrepCustomTemplate},
            {name: v.singleFramePrepCustomTemplateName, id: v.singleFramePrepCustomTemplate},
            {name: v.pairActiveDoorPrepCustomTemplateName, id: v.pairActiveDoorPrepCustomTemplate},
            {name: v.pairInactiveDoorPrepCustomTemplateName, id: v.pairInactiveDoorPrepCustomTemplate},
            {name: v.pairBothActiveDoorPrepCustomTemplateName, id: v.pairBothActiveDoorPrepCustomTemplate},
            {name: v.pairFramePrepCustomTemplateName, id: v.pairFramePrepCustomTemplate},
        ].filter(v => !!v.id) as { id: number; name: string }[], v => v.id.toString());
    }, [v])

    const onChangeRef = useSyncedRef(props.onChange)

    const opts = useMemo(() => {
        const stdList: {
            display: string;
            value: string;
            onClick(): void;
        }[] = [];

        const customList: {
            display: string;
            value: string;
            onClick(): void;
        }[] = [];

        stdOpts.forEach(o => {
            let value = o;

            stdList.push({
                display: o,
                value: value,
                onClick: () => {
                    onChangeRef.current({
                        [props.editKey]: value,
                        [props.editKeyCustom]: null,
                    })
                },
            })
        });

        customOpts.forEach(o => {
            let value = asPerTemplate;

            customList.push({
                display: o,
                value: value,
                onClick: () => {
                    onChangeRef.current({
                        [props.editKey]: value,
                        [props.editKeyCustom]: null,
                    });

                    if (listOfFiles.length === 0) {
                        setShowTemplate(true);
                    }
                }
            })
        })

        let errMissing: {display: string; value: string; onClick(): void} | null = null;
        if(!customList.find(v => v.value === value) && !stdList.find(v => v.value === value) && !!value) {
            errMissing = {
                display: `${value} (Error: unexpected value)`,
                value: value,
                onClick: () => {},
            };
        }

        return {
            stdList,
            customList,
            errMissing,
        };
    }, [
        value,
        stdOpts,
        customOpts,
        listOfFiles,
        onChangeRef,
        props.editKey,
        props.editKeyCustom
    ]);

    const isCustom = !!opts.customList.find(v => v.value === value);
    const selectedFile = listOfFiles.find(v => v.id === props.value[props.editKeyCustom] as any as number);

    return (
        <Grid container spacing={1} flexWrap="nowrap">
            <Grid item>
                <FormControl fullWidth>
                    <InputLabel id={props.id}>{props.name}</InputLabel>
                    <Select labelId={props.id} size="small" fullWidth label={props.name}
                            style={{minWidth: 150}}
                            value={value}>
                        {opts.errMissing && <MenuItem value={opts.errMissing.value}>
                            {opts.errMissing.value === noPrep ? "Choose a value" : opts.errMissing.display}
                        </MenuItem>}
                        {opts.errMissing && <Divider />}
                        {opts.stdList.map(o => <MenuItem value={o.value} onClick={() => o.onClick()}>
                            {o.display}
                        </MenuItem>)}
                        {opts.stdList.length > 0 && opts.customList.length > 0 && <Divider />}
                        {opts.customList.map(o => <MenuItem value={o.value} onClick={() => o.onClick()}>
                            {o.display}
                        </MenuItem>)}
                    </Select>
                </FormControl>
            </Grid>
            {isCustom && <Grid item>
                <FormControl fullWidth>
                    <InputLabel id={props.id + "_file"}>Prep File</InputLabel>
                    <Select labelId={props.id + "_file"} size="small" fullWidth label="Prep File"
                            value={(props.value[props.editKeyCustom] || "-").toString()}
                            onChange={e => {
                                const value = e.target.value as any as string;
                                if (value === "-") return;
                                if (value === "upload") return;

                                props.onChange({
                                    [props.editKeyCustomName]: listOfFiles.find(v => v.id?.toString() === value)?.name,
                                    [props.editKeyCustom]: parseInt(value, 10),
                                })
                            }}>
                        <MenuItem value={"-"}>Choose Template</MenuItem>
                        {listOfFiles.map(f => <MenuItem key={f.id} value={f.id?.toString()}>{f.name}</MenuItem>)}
                        <MenuItem value="upload" onClick={() => {
                            setShowTemplate(true);
                        }}>Upload File</MenuItem>
                    </Select>
                </FormControl>
            </Grid>}
            {selectedFile && <Grid item>
                <a target="_blank" rel="noreferrer" href={api.files.downloadLink(selectedFile.id, "inline")}>
                    <IconButton>
                        <OpenInNewIcon/>
                    </IconButton>
                </a>
            </Grid>}
            {showTemplate && <ProductFileLookup
                title={"Choose a " + props.name + " template"}
                onDone={file => {
                    setShowTemplate(false);
                    if (!file) return;

                    props.onChange({
                        [props.editKeyCustomName]: file.name,
                        [props.editKeyCustom]: file.id,
                    })
                }}
            />}
        </Grid>
    )
}

function autoFix(v: string): string {
    if(v.endsWith("See attached template")) {
        return asPerTemplate;
    }

    return v;
}